import { observable } from '@legendapp/state';
import { logger } from '@/lib/utils/logger';
import { Session } from 'next-auth';
import { paymentsService } from '@/lib/services';

// Types
interface Subscription {
  id: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  priceId: string;
}

interface PaymentHistory {
  id: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
}

interface PaginatedPaymentHistory {
  items: PaymentHistory[];
  total: number;
  limit: number;
  page: number;
}

interface CheckoutSession {
  url: string;
}

export interface PaymentsState {
  subscription: {
    data: Subscription | null;
    isLoading: boolean;
    error: Error | null;
  };
  paymentHistory: {
    data: PaginatedPaymentHistory | null;
    isLoading: boolean;
    error: Error | null;
  };
  checkout: {
    url: string | null;
    isLoading: boolean;
    error: Error | null;
  };
  session: Session | null;
}

// Initial state
const initialState: PaymentsState = {
  subscription: {
    data: null,
    isLoading: false,
    error: null,
  },
  paymentHistory: {
    data: null,
    isLoading: false,
    error: null,
  },
  checkout: {
    url: null,
    isLoading: false,
    error: null,
  },
  session: null,
};

// Create the observable store
export const paymentsState = observable<PaymentsState>(initialState);

// Action creators
export const paymentsActions = {
  // Session management
  setSession: (session: Session | null) => {
    logger.debug('[paymentsStore] Setting session', { session });
    paymentsState.session.set(session);
  },

  // Subscription actions
  setSubscription: (subscription: Subscription | null) => {
    logger.debug('[paymentsStore] Setting subscription', { subscription });
    paymentsState.subscription.data.set(subscription);
  },

  startSubscriptionLoading: () => {
    logger.debug('[paymentsStore] Starting subscription loading');
    paymentsState.subscription.isLoading.set(true);
    paymentsState.subscription.error.set(null);
  },

  setSubscriptionError: (error: Error) => {
    logger.debug('[paymentsStore] Setting subscription error', { error });
    paymentsState.subscription.isLoading.set(false);
    paymentsState.subscription.error.set(error);
  },

  completeSubscriptionLoading: () => {
    logger.debug('[paymentsStore] Completing subscription loading');
    paymentsState.subscription.isLoading.set(false);
  },

  // Payment history actions
  setPaymentHistory: (history: PaginatedPaymentHistory | null) => {
    logger.debug('[paymentsStore] Setting payment history', { history });
    paymentsState.paymentHistory.data.set(history);
  },

  startPaymentHistoryLoading: () => {
    logger.debug('[paymentsStore] Starting payment history loading');
    paymentsState.paymentHistory.isLoading.set(true);
    paymentsState.paymentHistory.error.set(null);
  },

  setPaymentHistoryError: (error: Error) => {
    logger.debug('[paymentsStore] Setting payment history error', { error });
    paymentsState.paymentHistory.isLoading.set(false);
    paymentsState.paymentHistory.error.set(error);
  },

  completePaymentHistoryLoading: () => {
    logger.debug('[paymentsStore] Completing payment history loading');
    paymentsState.paymentHistory.isLoading.set(false);
  },

  // Checkout actions
  setCheckoutUrl: (url: string | null) => {
    logger.debug('[paymentsStore] Setting checkout URL', { url });
    paymentsState.checkout.url.set(url);
  },

  startCheckoutLoading: () => {
    logger.debug('[paymentsStore] Starting checkout loading');
    paymentsState.checkout.isLoading.set(true);
    paymentsState.checkout.error.set(null);
  },

  setCheckoutError: (error: Error) => {
    logger.debug('[paymentsStore] Setting checkout error', { error });
    paymentsState.checkout.isLoading.set(false);
    paymentsState.checkout.error.set(error);
  },

  completeCheckoutLoading: () => {
    logger.debug('[paymentsStore] Completing checkout loading');
    paymentsState.checkout.isLoading.set(false);
  },

  reset: () => {
    logger.debug('[paymentsStore] Resetting state');
    paymentsState.set(initialState);
  },

  // Service-connected actions
  getSubscription: async () => {
    const session = paymentsState.session.get();
    if (!session) return;

    try {
      paymentsActions.startSubscriptionLoading();
      const subscription = await paymentsService.getSubscription(session);
      paymentsActions.setSubscription(subscription);
      return subscription;
    } catch (error) {
      logger.error('[paymentsStore] Failed to get subscription:', error);
      paymentsActions.setSubscriptionError(error as Error);
      throw error;
    } finally {
      paymentsActions.completeSubscriptionLoading();
    }
  },

  createSubscription: async (priceId: string) => {
    const session = paymentsState.session.get();
    if (!session) return;

    try {
      paymentsActions.startSubscriptionLoading();
      const subscription = await paymentsService.createSubscription(
        { priceId },
        session
      );
      paymentsActions.setSubscription(subscription);
      return subscription;
    } catch (error) {
      logger.error('[paymentsStore] Failed to create subscription:', error);
      paymentsActions.setSubscriptionError(error as Error);
      throw error;
    } finally {
      paymentsActions.completeSubscriptionLoading();
    }
  },

  updateSubscription: async (priceId: string) => {
    const session = paymentsState.session.get();
    if (!session) return;

    try {
      paymentsActions.startSubscriptionLoading();
      const subscription = await paymentsService.updateSubscription(
        { priceId },
        session
      );
      paymentsActions.setSubscription(subscription);
      return subscription;
    } catch (error) {
      logger.error('[paymentsStore] Failed to update subscription:', error);
      paymentsActions.setSubscriptionError(error as Error);
      throw error;
    } finally {
      paymentsActions.completeSubscriptionLoading();
    }
  },

  downgradeToFree: async () => {
    const session = paymentsState.session.get();
    if (!session) return;

    try {
      paymentsActions.startSubscriptionLoading();
      await paymentsService.downgradeToFree(session);
      paymentsActions.setSubscription(null);
    } catch (error) {
      logger.error('[paymentsStore] Failed to downgrade subscription:', error);
      paymentsActions.setSubscriptionError(error as Error);
      throw error;
    } finally {
      paymentsActions.completeSubscriptionLoading();
    }
  },

  getPaymentHistory: async (page: number = 1, limit: number = 10) => {
    const session = paymentsState.session.get();
    if (!session) return;

    try {
      paymentsActions.startPaymentHistoryLoading();
      const history = await paymentsService.getPaymentHistory(
        page,
        limit,
        session
      );
      paymentsActions.setPaymentHistory(history);
      return history;
    } catch (error) {
      logger.error('[paymentsStore] Failed to get payment history:', error);
      paymentsActions.setPaymentHistoryError(error as Error);
      throw error;
    } finally {
      paymentsActions.completePaymentHistoryLoading();
    }
  },

  createCheckoutSession: async (priceId: string) => {
    const session = paymentsState.session.get();
    if (!session) return;

    try {
      paymentsActions.startCheckoutLoading();
      const checkoutSession = await paymentsService.createCheckoutSession(
        priceId,
        session
      );
      if (!checkoutSession) {
        throw new Error('Failed to create checkout session');
      }
      paymentsActions.setCheckoutUrl(checkoutSession.url);
      return checkoutSession;
    } catch (error) {
      logger.error('[paymentsStore] Failed to create checkout session:', error);
      paymentsActions.setCheckoutError(error as Error);
      throw error;
    } finally {
      paymentsActions.completeCheckoutLoading();
    }
  },
};

// Selectors
export const paymentsSelectors = {
  getSubscription: () => paymentsState.subscription.data.get(),
  isSubscriptionLoading: () => paymentsState.subscription.isLoading.get(),
  getSubscriptionError: () => paymentsState.subscription.error.get(),
  getPaymentHistory: () => paymentsState.paymentHistory.data.get(),
  isPaymentHistoryLoading: () => paymentsState.paymentHistory.isLoading.get(),
  getPaymentHistoryError: () => paymentsState.paymentHistory.error.get(),
  getCheckoutUrl: () => paymentsState.checkout.url.get(),
  isCheckoutLoading: () => paymentsState.checkout.isLoading.get(),
  getCheckoutError: () => paymentsState.checkout.error.get(),
  getSession: () => paymentsState.session.get(),
};
